import {
  Chat,
  ChatMessageSendEvent,
  ChatMessageTemplateProps
} from '@progress/kendo-react-conversational-ui';
import { Avatar } from '@progress/kendo-react-layout';
import React, { useCallback, useContext, useRef } from 'react';

import {
  PlayerChatContext,
  PlayerChatContextProps
} from '../../../contexts/player-chat';
import { PlayerState } from '../../../types/state';
import { PlayerContext } from '../../../contexts/player';
import MessageChat from '../../../components/chat/message-chat';
import { ChatState } from '../../../types/state/websocket/chat-state';
import { GameDocumentContext } from '../../../contexts/game-document';
import { DisplayLanguageContext } from '../../../contexts/display-languages';
import { MessageTemplate } from '../../../components/chat/message-template';
import { generateTitleById } from '../../../utils/game-document/display-languages';
import { GetChatHistoryBetweeenPlayersAsync } from '../../../services/players';
import { useParams } from 'react-router-dom';
import { NotificationIndicator } from '../../notification-indicator';

interface ChatRoom {
  selectedPlayer: string;
  visibleButtonChat: boolean;
  playerList: PlayerState[];
  onChangePlayer: (code: string) => Promise<void>;
  addNewMessage: (event: ChatMessageSendEvent) => Promise<void>;
}

export const DirectChat = ({
  playerList,
  selectedPlayer,
  visibleButtonChat,
  addNewMessage,
  onChangePlayer
}: ChatRoom) => {
  const { gameCode } = useParams();

  const { newChat, chatHub } = React.useContext(
    PlayerChatContext
  ) as PlayerChatContextProps;
  const [gameDocumentstate] = useContext(GameDocumentContext);
  const [displayLanguageContext] = useContext(DisplayLanguageContext);
  const [playerState] = useContext(PlayerContext);

  const [messages, setMessages] = React.useState<ChatState[]>([]);

  const playerCode = playerState.playerState?.code ?? '';
  const playerName = playerState.playerState?.name ?? '';
  const user = {
    id: playerCode,
    avatarUrl: playerState?.playerState?.avatarImage ?? ''
  };

  const renderMessageTemplate = useCallback(
    (props: ChatMessageTemplateProps) => (
      <MessageTemplate
        hideChatButton={true}
        currentUserCode={user.id!}
        {...props}
      />
    ),
    [user.id]
  );

  const isNewDirectChatCount = useCallback(
    (code: string) =>
      newChat.filter((c) => c.group === 'player-chat' && c.author.id === code)
        .length,
    [newChat.length]
  );

  const getChatHistoryAsync = async (code: string) => {
    let newChats: ChatState[] = [];
    GetChatHistoryBetweeenPlayersAsync(gameCode!, playerCode, code).then(
      (response) => {
        response?.forEach((chat) => {
          newChats.push({
            author: {
              id: chat.playerCode,
              name: chat.playerName ?? '',
              avatarUrl: chat.playerAvatar ?? ''
            },
            timestamp: new Date(chat.sentDateUtc),
            group: 'player-chat',
            text: chat.message
          });
        });

        setMessages(newChats);
      }
    );
  };

  React.useEffect(() => {
    if (!chatHub || !gameCode || !selectedPlayer) return;

    chatHub.on('PrivateChatReceived', (data) => {
      const { senderCode, senderName, senderAvatar, message, sentDateUtc } =
        data;

      if (selectedPlayer === senderCode) {
        const chat = {
          author: {
            id: senderCode,
            name: senderName,
            avatarUrl: senderAvatar
          },
          timestamp: new Date(sentDateUtc),
          group: 'player-chat',
          text: message
        };
        setMessages((prev) => [...prev, chat]);
      }
    });
  }, [chatHub, gameCode, selectedPlayer]);

  React.useEffect(() => {
    getChatHistoryAsync(selectedPlayer);
  }, [selectedPlayer]);

  return (
    <div className="h-100 w-100 row m-0 p-0 flex-grow-1 overflow-hidden">
      <div className="col-3 d-none d-md-block pl-0 pr-3 h-100 overflow-auto d-flex flex-column">
        <div className="list-group w-100">
          {playerList.map((item) => {
            const counter = isNewDirectChatCount(item.code ?? '');
            return (
              <div
                key={item.code}
                className={`list-group-item cursor-pointer ${
                  selectedPlayer === item.code ? 'selected-list' : ''
                }`}
                onClick={() => {
                  onChangePlayer(item.code!);
                }}>
                <div
                  className={
                    'd-flex align-items-center justify-content-between'
                  }>
                  <div className={'d-flex gap-2 align-items-center'}>
                    <Avatar type={'image'} themeColor={'light'}>
                      <img
                        src={item.avatarImage}
                        className={` rounded-circle`}
                        alt={'Player Avatar'}
                      />
                    </Avatar>
                    {item?.name}
                  </div>
                  {counter > 0 && (
                    <NotificationIndicator
                      counter={counter}
                      bgColor={'#4698CB'}
                    />
                  )}
                </div>
              </div>
            );
          })}
        </div>
      </div>

      <div className={`col-12 col-md-9 m-0 p-0`}>
        <div className="d-flex h-100 facilitator-chat-container">
          <Chat
            className={`${!visibleButtonChat ? 'hide-action' : ''} flex-grow-1 w-100 h-100`}
            user={user}
            messages={messages}
            onMessageSend={(e) => {
              addNewMessage(e);

              const newMessage = {
                author: {
                  id: e.message.author.id,
                  name: e.message.author.name,
                  avatarUrl: e.message.author.avatarUrl
                },
                timestamp: e.message.timestamp,
                group: 'player-chat',
                text: e.message.text
              };
              setMessages((prev) => [...prev, newMessage]);
            }}
            placeholder={
              generateTitleById(
                '2373ccd3-e73e-403d-bd99-7083f1512a96',
                gameDocumentstate,
                displayLanguageContext.displayLanguageSelected.resources!,
                'game'
              ) || 'Type a message...'
            }
            message={MessageChat}
            messageTemplate={renderMessageTemplate}
          />
        </div>
      </div>
    </div>
  );
};
